import { createTheme } from '@mui/material/styles';

// Create a theme instance
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#A6C8D9',
      light: '#C2DCE9',
      dark: '#7A95A3',
    },
    secondary: {
      main: '#9FDBC1',
      light: '#B5E5D0',
      dark: '#7BA594',
    },
    success: {
      main: '#4CAF50',
      light: '#81C784',
      dark: '#388E3C',
    },
    warning: {
      main: '#FCA5A5',
      light: '#FDBBBB',
      dark: '#E57373',
    },
    error: {
      main: '#F9A8D4',
      light: '#FBCFE8',
      dark: '#EC4899',
    },
    background: {
      default: '#1A1F35',
      paper: '#293556',
      card: '#293556',
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
  },
  typography: {
    fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
    },
    h2: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
    },
    h3: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
    },
    h4: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
    },
    h5: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
    },
    h6: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
    },
    subtitle1: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    subtitle2: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    body1: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    body2: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    button: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
      textTransform: 'none',
      fontWeight: 500,
    },
    caption: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    overline: {
      fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
    },
  },
  shape: {
    borderRadius: 12, // Reduced for more subtle rounding
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          borderRadius: 24,
          padding: '10px 20px',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          fontWeight: 600,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          background: '#1A1F35',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8, // Less rounded corners for cards
          backgroundColor: 'rgba(126, 139, 145, 0.2)',
          backdropFilter: 'blur(8px)',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 24,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#A6C8D9',
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#A6C8D9',
                boxShadow: '0 0 0 3px rgba(166, 200, 217, 0.2)',
              },
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(126, 139, 145, 0.2)',
          backdropFilter: 'blur(8px)',
          borderRadius: 8, // Reduced border radius
        },
        elevation1: {
          boxShadow: 'none',
        },
        elevation2: {
          boxShadow: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          transition: 'all 0.2s ease',
          '&.Mui-selected': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          fontWeight: 500,
        },
      },
    },
    // Additional components for the financial app
    MuiTypography: {
      styleOverrides: {
        root: {
          "&.big-number": {
            fontFamily: '"Barlow", system-ui, sans-serif',
            fontWeight: 900,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
          // Ensure proper spacing for icons
          padding: 8,
          margin: 2,
          // Prevent icon overlap with proper sizing
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
  // Custom theme extensions for the financial app
  customSpendingCard: {
    background: 'rgba(126, 139, 145, 0.2)',
    backdropFilter: 'blur(8px)',
    borderRadius: '8px', // Reduced border radius to match screenshot
    overBudgetGradient: 'linear-gradient(to bottom, #F9A8D4, #FCA5A5)',
    underBudgetGradient: 'linear-gradient(to bottom, #9FDBC1, #A6C8D9)',
  },
});

export default theme;